import type { FileEntry } from '@hearth/shared';

/** Normalise a directory path: leading "/", no trailing "/", no "." or ".." segments. */
export function normalizeDir(path: string): string {
  const out: string[] = [];
  for (const part of path.split('/')) {
    if (!part || part === '.') continue;
    if (part === '..') out.pop();
    else out.push(part);
  }
  return `/${out.join('/')}`;
}

export function joinPath(dir: string, name: string): string {
  return normalizeDir(`${dir}/${name}`);
}

export function parentOf(path: string): string {
  return normalizeDir(`${path}/..`);
}

export function baseName(path: string): string {
  return normalizeDir(path).split('/').pop() ?? '';
}

/** Breadcrumbs for a directory: [{ name: 'plugins', path: '/plugins' }, ...]. */
export function crumbs(dir: string): { name: string; path: string }[] {
  const parts = normalizeDir(dir).split('/').filter(Boolean);
  return parts.map((name, i) => ({ name, path: `/${parts.slice(0, i + 1).join('/')}` }));
}

const ARCHIVE_RE = /\.(zip|tar|tar\.gz|tgz|tar\.xz|txz)$/i;
export const isArchive = (name: string) => ARCHIVE_RE.test(name);

const BINARY_EXT =
  /\.(jar|zip|gz|tgz|xz|txz|tar|7z|rar|png|jpe?g|gif|webp|ico|bmp|mp3|ogg|wav|mp4|mkv|dat|dat_old|mca|mcr|nbt|db|sqlite|so|dll|exe|bin|class|pak|vpk|ttf|otf|woff2?|pdf)$/i;

/** Whether the editor should open a file (text-like and within the 10 MiB limit). */
export function isEditable(e: FileEntry): boolean {
  if (!e.isFile || e.denied || e.size > 10 * 1024 * 1024) return false;
  if (BINARY_EXT.test(e.name)) return false;
  return (
    !/^(image|audio|video|font)\//.test(e.mimetype) &&
    !/zip|tar|gzip|x-7z|pdf|java-archive/.test(e.mimetype)
  );
}

export type EditorLanguage =
  | 'plaintext'
  | 'json'
  | 'yaml'
  | 'shell'
  | 'ini'
  | 'xml'
  | 'javascript'
  | 'python'
  | 'lua'
  | 'markdown'
  | 'html'
  | 'css'
  | 'sql'
  | 'dockerfile';

const LANGUAGES: [RegExp, EditorLanguage][] = [
  [/\.(json|mcmeta|json5)$/i, 'json'],
  [/\.(ya?ml)$/i, 'yaml'],
  [/\.(sh|bash|zsh|command)$/i, 'shell'],
  [/\.(properties|ini|cfg|conf|toml|env|lang)$/i, 'ini'],
  [/\.(xml|xsd|svg)$/i, 'xml'],
  [/\.(js|mjs|cjs|ts)$/i, 'javascript'],
  [/\.py$/i, 'python'],
  [/\.lua$/i, 'lua'],
  [/\.(md|markdown)$/i, 'markdown'],
  [/\.html?$/i, 'html'],
  [/\.css$/i, 'css'],
  [/\.sql$/i, 'sql'],
  [/(^|\/)Dockerfile$/, 'dockerfile'],
];

export function languageFor(name: string): EditorLanguage {
  return LANGUAGES.find(([re]) => re.test(name))?.[1] ?? 'plaintext';
}

/** Names game owners usually come looking for; shown as shortcuts when present at the root. */
const COMMON_RE =
  /^(server\.properties|server\.cfg|bukkit\.yml|spigot\.yml|paper(-global)?\.yml|purpur\.yml|ops\.json|whitelist\.json|banned-players\.json|eula\.txt|serverconfig\.txt|config|configs?|plugins|mods|world|logs|oxide|server)$/i;

/** The root entries worth a shortcut, from what is actually in the directory. */
export function commonEntries<T extends { name: string; denied: boolean }>(entries: T[]): T[] {
  return entries.filter((e) => !e.denied && COMMON_RE.test(e.name));
}

export type FileShortcut = 'select-all' | 'clear' | 'delete' | 'rename' | 'open' | 'up';

/**
 * The file manager's keyboard shortcuts: ⌘A/Ctrl+A select all, Esc clears, Delete or
 * Backspace deletes the selection, F2 renames and Enter opens a single selected entry,
 * Alt+↑ goes to the parent folder. Nothing fires while typing, in a menu or dialog, or
 * without the permission the action needs.
 */
export function fileShortcut(
  e: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>,
  ctx: {
    busy: boolean; // typing in a field, a menu or dialog is open
    selected: number;
    entries: number;
    atRoot: boolean;
    canDelete: boolean;
    canUpdate: boolean;
  },
): FileShortcut | null {
  if (ctx.busy) return null;
  const mod = e.metaKey || e.ctrlKey;
  if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'a')
    return ctx.entries > 0 ? 'select-all' : null;
  if (mod || e.shiftKey) return null;
  if (e.altKey) return e.key === 'ArrowUp' && !ctx.atRoot ? 'up' : null;
  switch (e.key) {
    case 'Escape':
      return ctx.selected > 0 ? 'clear' : null;
    case 'Delete':
    case 'Backspace':
      return ctx.selected > 0 && ctx.canDelete ? 'delete' : null;
    case 'F2':
      return ctx.selected === 1 && ctx.canUpdate ? 'rename' : null;
    case 'Enter':
      return ctx.selected === 1 ? 'open' : null;
    default:
      return null;
  }
}
